import NetInfo, { NetInfoStateType } from "@react-native-community/netinfo";
import { onPrepareTranslation } from "expo-translate-text";
import { useCallback } from "react";

import useStorageState from "$src/core/hooks/use-storage-state";
import useTranslation from "$src/core/hooks/use-translation";
import Alerting from "$src/core/services/alerting";
import Logging from "$src/core/services/logging/logging";
import SearchClassification from "$src/core/services/search-classification";
import AppleIntelligence from "$src/core/services/search-classification/apple-intelligence";
import ExecutorchEmbedding from "$src/core/services/search-classification/executorch-embedding";
import ExecutorchLlm from "$src/core/services/search-classification/executorch-llm";
import GoogleGeminiNano from "$src/core/services/search-classification/google-gemini-nano";
import type {
  ExecutorchEmbeddingService,
  ExecutorchLlmService,
} from "$src/core/services/search-classification/types";

type PreferredBackend =
  | "apple-intelligence"
  | "google-gemini-nano"
  | "executorch-embedding"
  | "executorch-llm";

type ExecutorchBackend = "executorch-embedding" | "executorch-llm";

const preferredBackend = (): PreferredBackend | null => {
  if (AppleIntelligence.status === "available") return "apple-intelligence";
  if (GoogleGeminiNano.status === "available") return "google-gemini-nano";
  if (ExecutorchEmbedding.status === "available") return "executorch-embedding";
  if (ExecutorchLlm.status === "available") return "executorch-llm";
  return null;
};

const executorchService = (
  backend: ExecutorchBackend,
): ExecutorchEmbeddingService | ExecutorchLlmService =>
  backend === "executorch-embedding" ? ExecutorchEmbedding : ExecutorchLlm;

const EXECUTORCH_SIZE_MB: Record<ExecutorchBackend, number> = {
  "executorch-embedding": 92,
  "executorch-llm": 505,
};

const useSearchClassificationConfiguration = () => {
  const { t, locale } = useTranslation();

  const [status, setStatus] = useStorageState(
    "search.conversational.status",
    null,
  );

  const enableExecutorch = useCallback(
    async (backend: ExecutorchBackend) => {
      const service = executorchService(backend);

      if (backend === "executorch-embedding") {
        const sourceLang = locale.split("-")[0]?.toLowerCase();
        if (sourceLang && sourceLang !== "en") {
          await onPrepareTranslation({
            sourceLangCode: sourceLang,
            targetLangCode: "en",
          });
        }
      }

      const download = await service.getDownload();
      if (download === "ready") {
        setStatus("enabled");
        service.startDownload().catch((error) => {
          Logging.error("Failed to load search model", { error, backend });
          Alerting.error(t("SEARCH/CONVERSATIONAL/DOWNLOAD_FAILED"));
          setStatus("disabled");
        });
        return;
      }

      const networkInfo = await NetInfo.fetch();
      if (!networkInfo.isConnected) {
        Alerting.error(t("SEARCH/CONVERSATIONAL/OFFLINE"));
        return;
      }

      if (networkInfo.type !== NetInfoStateType.wifi) {
        Alerting.error(t("SEARCH/CONVERSATIONAL/NOT_ON_WIFI"));
        return;
      }

      Alerting.prompt(
        t("SEARCH/CONVERSATIONAL/DOWNLOAD_SIZE_PROMPT", {
          sizeMb: EXECUTORCH_SIZE_MB[backend],
        }),
        {
          title: t("SEARCH/CONVERSATIONAL/LABEL"),
          confirmLabel: t("SEARCH/CONVERSATIONAL/DOWNLOAD"),
          cancelLabel: t("COMMON/CANCEL"),
          onConfirm: () => {
            setStatus("enabled");
            service.startDownload().catch((error) => {
              Logging.error("Failed to download search model", {
                error,
                backend,
              });
              Alerting.error(t("SEARCH/CONVERSATIONAL/DOWNLOAD_FAILED"));
              setStatus("disabled");
            });
          },
          onCancel: () => setStatus("disabled"),
        },
      );
    },
    [locale, setStatus, t],
  );

  const enable = useCallback(async () => {
    try {
      if (!SearchClassification.isSupported) {
        Alerting.error(t("SEARCH/CONVERSATIONAL/UNAVAILABLE"));
        setStatus("disabled");
        return;
      }

      const backend = preferredBackend();

      if (backend === "apple-intelligence") {
        setStatus("enabled");
        AppleIntelligence.prepare().catch((error) => {
          Logging.error("Failed to prepare Apple Intelligence", { error });
          setStatus("disabled");
        });
        return;
      }

      if (backend === "google-gemini-nano") {
        setStatus("enabled");
        GoogleGeminiNano.prepare().catch((error) => {
          Logging.error("Failed to prepare Google Gemini Nano", { error });
          setStatus("disabled");
        });
        return;
      }

      if (backend === "executorch-embedding" || backend === "executorch-llm") {
        await enableExecutorch(backend);
      }
    } catch (error) {
      Logging.error("Failed to enable conversational search", { error });
      setStatus("disabled");
    }
  }, [enableExecutorch, setStatus, t]);

  const disable = useCallback(() => {
    setStatus("disabled");
  }, [setStatus]);

  return {
    status,
    enable,
    disable,
  };
};

export default useSearchClassificationConfiguration;
